package io.github.retar.portfolio.pages

import androidx.compose.runtime.*
import com.varabyte.kobweb.core.Page
import com.varabyte.kobweb.core.data.*
import com.varabyte.kobweb.core.layout.Layout
import com.varabyte.kobweb.core.layout.NoLayout
import com.varabyte.kobweb.core.init.InitRoute
import com.varabyte.kobweb.core.init.InitRouteContext
import com.varabyte.kobwebx.frontmatter.*
import com.varabyte.kobwebx.markdown.*

@InitRoute
fun initStatic_kotlin_native_sqlcipherPage(ctx: InitRouteContext) {
    ctx.data.add(MarkdownContext("static_kotlin_native_sqlcipher.md", FrontMatterElement.Builder { addScalar("title", "Shipping SQLCipher inside a static Kotlin/Native framework (so the iOS team changed nothing)"); addScalar("description", "Baking SQLCipher into a static Kotlin/Native framework so the iOS team adds nothing, not even a linker flag."); addScalar("date", "2026-06-21"); addScalar("cover", "/articles/sqlcipher_kotlin_native.jpeg"); }))
}

@Page("/blog/sqlcipher-static-kotlin-native-framework")
@Layout(".pages.MarkdownPage")
@Composable
fun Static_kotlin_native_sqlcipherPage() {
    org.jetbrains.compose.web.dom.H1(attrs = { id("shipping-sqlcipher-inside-a-static-kotlin-native-framework-so-the-ios-team-changed-nothing") }) {
        org.jetbrains.compose.web.dom.Text("Shipping SQLCipher inside a static Kotlin/Native framework (so the iOS team changed nothing)")
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("I work on a KMP app. Shared code (networking, domain logic, a Room database) compiles into a single static framework the iOS app links against. One day the requirement landed: the on-device Room cache on iOS needs to be encrypted, same as it already is on Android with SQLCipher.")
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("The interesting part was the constraint. The iOS team shouldn't have to add a dependency, set a build flag, or manage SQLCipher at all. They link one framework today, they should link the same one tomorrow, and encryption comes with it. No SPM package, no ")
        io.github.retar.portfolio.components.widgets.InlineCode("""-force_load""")
        org.jetbrains.compose.web.dom.Text(" flag. They do still hand over the encryption key, but that's a runtime argument, a ")
        io.github.retar.portfolio.components.widgets.InlineCode("""ByteArray""")
        org.jetbrains.compose.web.dom.Text(", not a build change. I'll get to that.")
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("That constraint is why the standard recipe didn't work, and it's why this took me a while.")
    }
    org.jetbrains.compose.web.dom.H2(attrs = { id("why-the-standard-recipe-doesn-t-apply") }) {
        org.jetbrains.compose.web.dom.Text("Why the standard recipe doesn't apply")
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("Google \"SQLCipher iOS\" and you get the same thing everywhere:")
    }
    org.jetbrains.compose.web.dom.Ol {
        org.jetbrains.compose.web.dom.Li {
            org.jetbrains.compose.web.dom.Text("Add ")
            io.github.retar.portfolio.components.widgets.InlineCode("""SQLCipher-SPM""")
            org.jetbrains.compose.web.dom.Text(" as a Swift Package dependency.")
        }
        org.jetbrains.compose.web.dom.Li {
            org.jetbrains.compose.web.dom.Text("Pass ")
            io.github.retar.portfolio.components.widgets.InlineCode("""-force_load .../libsqlcipher.a""")
            org.jetbrains.compose.web.dom.Text(" to Xcode's linker.")
        }
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("That works because the ")
        org.jetbrains.compose.web.dom.Em {
            org.jetbrains.compose.web.dom.Text("app's")
        }
        org.jetbrains.compose.web.dom.Text(" linker is what resolves symbols. At app-link time it sees your code and the SQLCipher archive together and stitches them in. ")
        io.github.retar.portfolio.components.widgets.InlineCode("""-force_load""")
        org.jetbrains.compose.web.dom.Text(" forces every object in the archive so the encrypted ")
        io.github.retar.portfolio.components.widgets.InlineCode("""sqlite3_*""")
        org.jetbrains.compose.web.dom.Text(" symbols win over the system's plain SQLite.")
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("My situation breaks this in two ways:")
    }
    org.jetbrains.compose.web.dom.Ul {
        org.jetbrains.compose.web.dom.Li {
            io.github.retar.portfolio.components.widgets.InlineCode("""SharedKit""")
            org.jetbrains.compose.web.dom.Text(" is a ")
            org.jetbrains.compose.web.dom.Em {
                org.jetbrains.compose.web.dom.Text("static")
            }
            org.jetbrains.compose.web.dom.Text(" framework (")
            io.github.retar.portfolio.components.widgets.InlineCode("""isStatic = true""")
            org.jetbrains.compose.web.dom.Text("). There's no app-level link step I control the way that recipe assumes. By the time the iOS app links it, the ")
            io.github.retar.portfolio.components.widgets.InlineCode("""sqlite3_*""")
            org.jetbrains.compose.web.dom.Text(" symbols inside the framework are already set in stone.")
        }
        org.jetbrains.compose.web.dom.Li {
            org.jetbrains.compose.web.dom.Text("The iOS team adds nothing. Pushing an SPM package and a linker flag onto them is exactly what I was told to avoid.")
        }
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("But there's a subtler problem. Room's iOS support comes through ")
        io.github.retar.portfolio.components.widgets.InlineCode("""androidx.sqlite:sqlite-framework""")
        org.jetbrains.compose.web.dom.Text(". That artifact gives me the cinterop bindings my driver uses (")
        io.github.retar.portfolio.components.widgets.InlineCode("""NativeSQLiteConnection""")
        org.jetbrains.compose.web.dom.Text(", ")
        io.github.retar.portfolio.components.widgets.InlineCode("""sqlite3_open_v2""")
        org.jetbrains.compose.web.dom.Text(", and friends), but it binds them against the ")
        org.jetbrains.compose.web.dom.B {
            org.jetbrains.compose.web.dom.Text("system")
        }
        org.jetbrains.compose.web.dom.Text(" ")
        io.github.retar.portfolio.components.widgets.InlineCode("""libsqlite3""")
        org.jetbrains.compose.web.dom.Text(" baked into iOS. System SQLite has no encryption codec. SQLCipher is a drop-in replacement: same ")
        io.github.retar.portfolio.components.widgets.InlineCode("""sqlite3_*""")
        org.jetbrains.compose.web.dom.Text(" symbol names, but with encryption. The challenge is getting SQLCipher's ")
        io.github.retar.portfolio.components.widgets.InlineCode("""sqlite3_*""")
        org.jetbrains.compose.web.dom.Text(" symbols to win over the system ones at the point my framework resolves them.")
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("So this is a symbol-resolution fight. That's why ")
        io.github.retar.portfolio.components.widgets.InlineCode("""-force_load libsqlcipher.a""")
        org.jetbrains.compose.web.dom.Text(" as a Kotlin/Native ")
        io.github.retar.portfolio.components.widgets.InlineCode("""linkerOpt""")
        org.jetbrains.compose.web.dom.Text(" was the wrong tool, even though that's where I started.")
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("The symbols had to get ")
        org.jetbrains.compose.web.dom.Em {
            org.jetbrains.compose.web.dom.Text("into")
        }
        org.jetbrains.compose.web.dom.Text(" ")
        io.github.retar.portfolio.components.widgets.InlineCode("""SharedKit""")
        org.jetbrains.compose.web.dom.Text(" some other way.")
    }
    org.jetbrains.compose.web.dom.H2(attrs = { id("building-sqlcipher-as-a-static-library") }) {
        org.jetbrains.compose.web.dom.Text("Building SQLCipher as a static library")
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("Nothing exotic here. A script clones a pinned SQLCipher tag and compiles it per arch (")
        io.github.retar.portfolio.components.widgets.InlineCode("""ios-arm64""")
        org.jetbrains.compose.web.dom.Text(", ")
        io.github.retar.portfolio.components.widgets.InlineCode("""ios-simulator-arm64""")
        org.jetbrains.compose.web.dom.Text(") with Apple's CommonCrypto as the crypto backend, so there's no OpenSSL to vendor:")
    }
    io.github.retar.portfolio.components.widgets.CodeBlock("""./configure \
    --host="${'$'}host" \
    --with-crypto-lib=commoncrypto \
    --disable-shared --enable-static \
    --disable-tcl \
    CFLAGS="-arch ${'$'}arch -isysroot ${'$'}sysroot ${'$'}min_version_flag ${'$'}target_flag \
        -DSQLITE_HAS_CODEC \
        -DSQLCIPHER_CRYPTO_CC \
        -DSQLITE_TEMP_STORE=2 \
        ... -Os" \
    LDFLAGS="-arch ${'$'}arch -isysroot ${'$'}sysroot ... -framework Security"

make -j"${'$'}(sysctl -n hw.ncpu)" libsqlcipher.la
cp .libs/libsqlcipher.a "${'$'}output_dir/libsqlcipher.a"
""", lang = "bash")
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("The flags that matter: ")
        io.github.retar.portfolio.components.widgets.InlineCode("""-DSQLITE_HAS_CODEC""")
        org.jetbrains.compose.web.dom.Text(" turns on the encryption codec, and ")
        io.github.retar.portfolio.components.widgets.InlineCode("""-DSQLCIPHER_CRYPTO_CC""")
        org.jetbrains.compose.web.dom.Text(" (with ")
        io.github.retar.portfolio.components.widgets.InlineCode("""--with-crypto-lib=commoncrypto""")
        org.jetbrains.compose.web.dom.Text(") routes crypto through CommonCrypto, which lives in Apple's ")
        io.github.retar.portfolio.components.widgets.InlineCode("""Security""")
        org.jetbrains.compose.web.dom.Text(" framework. Out comes ")
        io.github.retar.portfolio.components.widgets.InlineCode("""libsqlcipher.a""")
        org.jetbrains.compose.web.dom.Text(" per arch. These are gitignored and built on demand, not checked in.")
    }
    org.jetbrains.compose.web.dom.H2(attrs = { id("merging-sqlcipher-into-the-framework-binary") }) {
        org.jetbrains.compose.web.dom.Text("Merging SQLCipher into the framework binary")
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("This is the move nobody documents, and I got it wrong before I got it right.")
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("My first instinct was to port the standard recipe into Gradle: ")
        io.github.retar.portfolio.components.widgets.InlineCode("""linkerOpts("-force_load", ".../libsqlcipher.a")""")
        org.jetbrains.compose.web.dom.Text(" per iOS target. Felt right. It's wrong. ")
        io.github.retar.portfolio.components.widgets.InlineCode("""-force_load""")
        org.jetbrains.compose.web.dom.Text(" is a flag for ")
        io.github.retar.portfolio.components.widgets.InlineCode("""ld""")
        org.jetbrains.compose.web.dom.Text(", the linker that produces a final executable or dynamic image. A ")
        org.jetbrains.compose.web.dom.B {
            org.jetbrains.compose.web.dom.Text("static")
        }
        org.jetbrains.compose.web.dom.Text(" framework never goes through that step inside our build. ")
        io.github.retar.portfolio.components.widgets.InlineCode("""isStatic = true""")
        org.jetbrains.compose.web.dom.Text(" means the framework binary is itself a static archive, assembled with ")
        io.github.retar.portfolio.components.widgets.InlineCode("""ar""")
        org.jetbrains.compose.web.dom.Text(" and ")
        io.github.retar.portfolio.components.widgets.InlineCode("""libtool""")
        org.jetbrains.compose.web.dom.Text(", not linked with ")
        io.github.retar.portfolio.components.widgets.InlineCode("""ld""")
        org.jetbrains.compose.web.dom.Text(". There's no ")
        io.github.retar.portfolio.components.widgets.InlineCode("""ld""")
        org.jetbrains.compose.web.dom.Text(" invocation for ")
        io.github.retar.portfolio.components.widgets.InlineCode("""-force_load""")
        org.jetbrains.compose.web.dom.Text(" to attach to. The flag gets ignored, or only matters at the iOS app's final link, which is exactly the iOS-side step I'm trying to eliminate.")
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("So the right tool isn't a linker flag at all. If the framework binary is a static archive, then embedding SQLCipher means ")
        org.jetbrains.compose.web.dom.B {
            org.jetbrains.compose.web.dom.Text("merging two static archives into one")
        }
        org.jetbrains.compose.web.dom.Text(", a job for ")
        io.github.retar.portfolio.components.widgets.InlineCode("""ar""")
        org.jetbrains.compose.web.dom.Text(" and ")
        io.github.retar.portfolio.components.widgets.InlineCode("""libtool""")
        org.jetbrains.compose.web.dom.Text(", the same tools that built the framework in the first place. It's the same kind of operation the build system already does, just one stage later.")
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("After Gradle assembles the XCFramework, a ")
        io.github.retar.portfolio.components.widgets.InlineCode("""finalizedBy""")
        org.jetbrains.compose.web.dom.Text(" task runs for each slice of the ")
        io.github.retar.portfolio.components.widgets.InlineCode(""".xcframework""")
        org.jetbrains.compose.web.dom.Text(", takes the assembled framework binary, and re-combines it with SQLCipher's objects:")
    }
    io.github.retar.portfolio.components.widgets.CodeBlock("""// 1. Explode the framework binary back into its object files.
exec("ar", "x", binary.absolutePath, workingDir = extractFramework)

// 2. Explode the SQLCipher static lib into its object files.
exec("ar", "x", sqlcipherLib.absolutePath, workingDir = extractSqlcipher)

// 3. Relocatable-link everything into one combined object.
val objectFiles = (extractFramework.listFiles() + extractSqlcipher.listFiles())
    .filter { file -> file.extension == "o" }
val merged = tmpDir.resolve("merged.o")
exec(listOf("ld", "-r", "-arch", "arm64", "-o", merged.absolutePath) +
    objectFiles.map { it.absolutePath })

// 4. Re-wrap as a static archive, overwriting the framework binary in place.
exec("libtool", "-static", "-o", binary.absolutePath, merged.absolutePath)
""", lang = "kotlin")
    org.jetbrains.compose.web.dom.P {
        io.github.retar.portfolio.components.widgets.InlineCode("""ar x""")
        org.jetbrains.compose.web.dom.Text(" unpacks an archive into its ")
        io.github.retar.portfolio.components.widgets.InlineCode(""".o""")
        org.jetbrains.compose.web.dom.Text(" members. ")
        io.github.retar.portfolio.components.widgets.InlineCode("""ld -r""")
        org.jetbrains.compose.web.dom.Text(" does a partial (relocatable) link, combining many objects into one without producing a final executable. ")
        io.github.retar.portfolio.components.widgets.InlineCode("""libtool -static""")
        org.jetbrains.compose.web.dom.Text(" wraps that back into the archive that ")
        org.jetbrains.compose.web.dom.Em {
            org.jetbrains.compose.web.dom.Text("is")
        }
        org.jetbrains.compose.web.dom.Text(" the framework binary. The net effect: SQLCipher's ")
        io.github.retar.portfolio.components.widgets.InlineCode("""sqlite3_open_v2""")
        org.jetbrains.compose.web.dom.Text(", the codec, the ")
        io.github.retar.portfolio.components.widgets.InlineCode("""PRAGMA key""")
        org.jetbrains.compose.web.dom.Text(" handler, all physically inside the framework.")
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("Wired into the build so it runs automatically after assembly:")
    }
    io.github.retar.portfolio.components.widgets.CodeBlock("""tasks.matching { it.name == assembleTask }.configureEach {
    finalizedBy("mergeSqlCipher${'$'}{variant}XCFramework")
}
""", lang = "kotlin")
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("The only thing the framework itself has to declare is the system framework CommonCrypto lives in:")
    }
    io.github.retar.portfolio.components.widgets.CodeBlock("""linkerOpts("-S", "-framework", "Security")
""", lang = "kotlin")
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("That's the build-side iOS story. The iOS team links the ")
        io.github.retar.portfolio.components.widgets.InlineCode(""".xcframework""")
        org.jetbrains.compose.web.dom.Text(" exactly as before, no SPM package, no linker flags. The encrypted SQLite engine is already in it.")
    }
    org.jetbrains.compose.web.dom.H2(attrs = { id("using-it-and-proving-it-s-on") }) {
        org.jetbrains.compose.web.dom.Text("Using it, and proving it's on")
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("A custom ")
        io.github.retar.portfolio.components.widgets.InlineCode("""SQLiteDriver""")
        org.jetbrains.compose.web.dom.Text(" opens the connection via cinterop and immediately sets the key, then checks that the SQLite it's talking to is really SQLCipher:")
    }
    io.github.retar.portfolio.components.widgets.CodeBlock("""val connection = NativeSQLiteConnection(dbPointer.value!!)
connection.execSQL("PRAGMA key = \"x'${'$'}{encryptionKey.toHexString()}'\"")
verifyCipherAvailable(connection)
""", lang = "kotlin")
    io.github.retar.portfolio.components.widgets.CodeBlock("""private fun verifyCipherAvailable(connection: SQLiteConnection) {
    connection.prepare("PRAGMA cipher_version").use { stmt ->
        if (stmt.step()) {
            Logger.i { "SQLCipher active, version: ${'$'}{stmt.getText(0)}" }
        } else {
            Logger.w { "SQLCipher is NOT available. Database is NOT encrypted." }
        }
    }
}
""", lang = "kotlin")
    org.jetbrains.compose.web.dom.P {
        io.github.retar.portfolio.components.widgets.InlineCode("""PRAGMA cipher_version""")
        org.jetbrains.compose.web.dom.Text(" returns a row only on SQLCipher. On plain SQLite it returns nothing. If the merge ever silently stops working, this fires.")
    }
    org.jetbrains.compose.web.dom.H2(attrs = { id("the-one-thing-ios-still-owns-the-key") }) {
        org.jetbrains.compose.web.dom.Text("The one thing iOS still owns: the key")
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("\"iOS changes nothing\" is true for the SQLCipher integration: the engine and its linkage are fully inside the framework. But the ")
        io.github.retar.portfolio.components.widgets.InlineCode("""encryptionKey""")
        org.jetbrains.compose.web.dom.Text(" in the snippets above has to come from somewhere. The framework does the encryption. It does not invent or store the secret. That was deliberate:")
    }
    io.github.retar.portfolio.components.widgets.CodeBlock("""fun getDatabaseBuilder(
    name: String,
    encryptionKey: ByteArray,
    directoryPath: String? = null,
): RoomDatabase.Builder<AppDatabase>
""", lang = "kotlin")
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("The key arrives as a ")
        io.github.retar.portfolio.components.widgets.InlineCode("""ByteArray""")
        org.jetbrains.compose.web.dom.Text(" when the iOS app constructs the shared SDK entry point (something like ")
        io.github.retar.portfolio.components.widgets.InlineCode("""SharedSDK(httpTransport, encryptionKey, ...)""")
        org.jetbrains.compose.web.dom.Text("), and flows down to the driver. Generating a random key on first launch and persisting it in the iOS Keychain is a platform concern, so it stays on the iOS side. The framework has no business owning a Keychain item. The split:")
    }
    org.jetbrains.compose.web.dom.Ul {
        org.jetbrains.compose.web.dom.Li {
            org.jetbrains.compose.web.dom.Text("Framework owns the engine: SQLCipher, the driver, ")
            io.github.retar.portfolio.components.widgets.InlineCode("""PRAGMA key""")
            org.jetbrains.compose.web.dom.Text(", the encrypted reads/writes.")
        }
        org.jetbrains.compose.web.dom.Li {
            org.jetbrains.compose.web.dom.Text("iOS owns the secret: generate it, store it in the Keychain, hand it over as a ")
            io.github.retar.portfolio.components.widgets.InlineCode("""ByteArray""")
            org.jetbrains.compose.web.dom.Text(".")
        }
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("This is a one-argument contract, not a build-system change, so it doesn't violate the \"no SPM, no linker flags\" goal. It does have a consequence worth designing for: if the key ever stops matching the database (Keychain reset on reinstall, a key rotation), the existing file is undecryptable. SQLCipher surfaces that as \"file is not a database\" the first time it reads an encrypted page, deep inside Room's connection setup, where the error doesn't say \"your key is wrong.\"")
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("So before Room ever opens the file, I pre-flight it with the supplied key:")
    }
    io.github.retar.portfolio.components.widgets.CodeBlock("""internal fun recreateDatabaseIfKeyInvalid(fileName: String, encryptionKey: ByteArray) {
    if (!fileManager.fileExistsAtPath(fileName)) return        // fresh start, nothing to decrypt
    if (!canDecrypt(fileName, encryptionKey)) {                // wrong key → unrecoverable
        deleteDatabaseFiles(fileName, fileManager)             // drop it; Room recreates encrypted
    }
}
""", lang = "kotlin")
    org.jetbrains.compose.web.dom.P {
        io.github.retar.portfolio.components.widgets.InlineCode("""canDecrypt""")
        org.jetbrains.compose.web.dom.Text(" opens the file, sets the key, and forces a read of page 1 (")
        io.github.retar.portfolio.components.widgets.InlineCode("""SELECT count(*) FROM sqlite_master""")
        org.jetbrains.compose.web.dom.Text(") so a bad key trips ")
        org.jetbrains.compose.web.dom.Em {
            org.jetbrains.compose.web.dom.Text("here")
        }
        org.jetbrains.compose.web.dom.Text(" rather than inside Room. Because this database is a cache, deleting and recreating is safe. You lose cached data, not user data.")
    }
    org.jetbrains.compose.web.dom.H2(attrs = { id("sharp-edges") }) {
        org.jetbrains.compose.web.dom.Text("Sharp edges")
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("The worst one: ")
        org.jetbrains.compose.web.dom.B {
            org.jetbrains.compose.web.dom.Text("if you misconfigure it, you get plaintext instead of a build failure.")
        }
        org.jetbrains.compose.web.dom.Text(" The merge task just skips a slice if the ")
        io.github.retar.portfolio.components.widgets.InlineCode(""".a""")
        org.jetbrains.compose.web.dom.Text(" is missing:")
    }
    io.github.retar.portfolio.components.widgets.CodeBlock("""if (!binary.exists() || !sqlcipherLib.exists()) return@forEach
""", lang = "kotlin")
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("So if someone forgets to build the SQLCipher libs, the framework builds ")
        org.jetbrains.compose.web.dom.Em {
            org.jetbrains.compose.web.dom.Text("successfully")
        }
        org.jetbrains.compose.web.dom.Text(", just unencrypted. The only signal is the runtime log line from above, not a red build. An unencrypted framework that ships as if encrypted is the worst outcome I can think of here. The merge task should fail loudly when a ")
        io.github.retar.portfolio.components.widgets.InlineCode(""".a""")
        org.jetbrains.compose.web.dom.Text(" is missing instead of skipping. I haven't done that yet, and until I do, the runtime check is the only thing catching it.")
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("Two more:")
    }
    org.jetbrains.compose.web.dom.Ul {
        org.jetbrains.compose.web.dom.Li {
            org.jetbrains.compose.web.dom.Text("The system ")
            io.github.retar.portfolio.components.widgets.InlineCode("""libsqlite3""")
            org.jetbrains.compose.web.dom.Text(" and SQLCipher both define ")
            io.github.retar.portfolio.components.widgets.InlineCode("""sqlite3_*""")
            org.jetbrains.compose.web.dom.Text(". Merging SQLCipher's objects into the framework binary is what tips resolution toward SQLCipher, but a Room or Kotlin/Native version bump is exactly the kind of thing that can disturb that. The ")
            io.github.retar.portfolio.components.widgets.InlineCode("""PRAGMA cipher_version""")
            org.jetbrains.compose.web.dom.Text(" check is what guards it.")
        }
        org.jetbrains.compose.web.dom.Li {
            io.github.retar.portfolio.components.widgets.InlineCode("""-arch arm64""")
            org.jetbrains.compose.web.dom.Text(" is hardcoded in the ")
            io.github.retar.portfolio.components.widgets.InlineCode("""ld -r""")
            org.jetbrains.compose.web.dom.Text(" step, matching the two arm64 slices shipped (device + Apple-silicon simulator). Add an x86_64 simulator slice and this needs to learn about arch.")
        }
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("The runtime check is the only one actually in place today. Failing the build on a missing ")
        io.github.retar.portfolio.components.widgets.InlineCode(""".a""")
        org.jetbrains.compose.web.dom.Text(" and pinning the Room and Kotlin/Native versions are still on the to-do list.")
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("When you need a third-party static library baked into a static Kotlin/Native framework, the same ")
        io.github.retar.portfolio.components.widgets.InlineCode("""ar""")
        org.jetbrains.compose.web.dom.Text("/")
        io.github.retar.portfolio.components.widgets.InlineCode("""libtool""")
        org.jetbrains.compose.web.dom.Text(" archive operations that built the framework do the job, applied one stage later (")
        io.github.retar.portfolio.components.widgets.InlineCode("""ar x""")
        org.jetbrains.compose.web.dom.Text(" → ")
        io.github.retar.portfolio.components.widgets.InlineCode("""ld -r""")
        org.jetbrains.compose.web.dom.Text(" → ")
        io.github.retar.portfolio.components.widgets.InlineCode("""libtool -static""")
        org.jetbrains.compose.web.dom.Text("). ")
        io.github.retar.portfolio.components.widgets.InlineCode("""-force_load""")
        org.jetbrains.compose.web.dom.Text(" is for the final ")
        io.github.retar.portfolio.components.widgets.InlineCode("""ld""")
        org.jetbrains.compose.web.dom.Text(" link, and a static framework doesn't go through one inside its own build.")
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("The constraint \"iOS adds no dependency or build config\" drove the design. The easy answer, ")
        io.github.retar.portfolio.components.widgets.InlineCode("""addSpm""")
        org.jetbrains.compose.web.dom.Text(" plus ")
        io.github.retar.portfolio.components.widgets.InlineCode("""-force_load""")
        org.jetbrains.compose.web.dom.Text(", shifted the integration cost onto the iOS team. The build here absorbed it. iOS constructs the SDK with a ")
        io.github.retar.portfolio.components.widgets.InlineCode("""ByteArray""")
        org.jetbrains.compose.web.dom.Text(" encryption key and otherwise links the framework exactly as before.")
    }
}
